// HOPE TO REPLACE THIS FILE'S NAME WITH "JobTracker.js" IN THE FUTURE, BUT FOR NOW KEEPING AS "ApplicationsTracker.js" TO AVOID ANY ISSUES WITH ROUTING.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import api from '../api/api';
import { Filter, Search, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Applications from '../components/Applications';
import JobTrackerSkeleton from '../components/layout/JobTrackerSkeleton';

export default function ApplicationsTracker() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  // toggle between Focused and Archived job entries
  const [view, setView] = useState('focus'); // 'focus' | 'archive'
  const [debouncedView, setDebouncedView] = useState('focus')
  const isFocusView = debouncedView === 'focus'; // drives the actual fetch
  const isFocusViewImmediate = view === 'focus'; // drives the button highlight
  // const applications = isFocusView ? focuses : archives;
  
  // Filters
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // used to prevent multiple fetches when use filtering
  const [statusFilter, setStatusFilter] = useState("all");
  const [referralFilter, setReferralFilter] = useState("all");
  const [coverLetterFilter, setCoverLetterFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);


  const perPage = 10;

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedLocation) count += 1;
    if (selectedPosition) count += 1;
    if (dateFilter !== 'all') count += 1;
    if (statusFilter !== 'all') count += 1;
    if (referralFilter !== 'all') count += 1;
    if (coverLetterFilter !== 'all') count += 1;
    return count;
  }, [selectedLocation, selectedPosition, dateFilter, statusFilter, referralFilter, coverLetterFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setDebouncedSearchTerm(searchTerm) // immediate fetch when pressing Enter
  };

  const handleViewChange = (v) => {
      setView(v);
      setPage(1);
  };

  const accent = { badgeBg: 'bg-primary-100', badgeText: 'text-primary-700', hoverRow: isDarkMode ? 'hover:bg-primary-900/20' : 'hover:bg-primary-50', activeBtn: 'bg-primary-600 text-white' };
  const segBtnClass = (active) => `px-3 py-1 rounded-md text-sm font-medium transition-colors ${
      active
          ? accent.activeBtn
          : isDarkMode ? 'text-slate-300 hover:bg-slate-600' : 'text-slate-600 hover:bg-slate-200'
  }`;
  // a timer that resets every keystroke, only fetch after the user stop swicthing for 0.5s (Focus|Archive toggle)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedView(view);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [view]);
  
  // trigger loading UI when toggle between Focus and Archive display
  useEffect(() => {
    setLoading(true);
    setInitialLoad(true);
    // expecting fetchJobs() will set them back to false
  }, [view])

  // a timer that resets every keystroke, only fetch after the user stop typing for 0.5s
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleClearFilters = () => {
    setSelectedLocation('');
    setSelectedPosition('');
    setDateFilter('all');
    setStatusFilter('all');
    setReferralFilter('all');
    setCoverLetterFilter('all');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPage(1);
  };

  useEffect(() => {
    if (!user) {
        navigate('/login');
    }
  }, [navigate, user]);

  const handleApplyFilters = () => {
    setPage(1);
    setShowFilters(false);
  };

  const fetchJobs = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      setLoading(true);
      const params = {
          page,
          per_page: perPage,
          search: debouncedSearchTerm || undefined
      };
      
      if (selectedLocation) {
          params.location = selectedLocation;
      }
        if (selectedPosition) {
          params.position = selectedPosition;
        }
        if (dateFilter !== "all") {
          params.date = dateFilter;
        }

        params.active_status = isFocusView ? 1 : 0

        const response = await api.get('/jobs/tracker', { params });
        const rows = response.data?.jobs || [];

        const normalized = rows.map((row) => ({
        id: row.JobTrackerID,
        unique_job_id: row.UniqueJobID,
        title: row.JobTitle || 'Untitled Role',
        company: row.CompanyName || 'Unknown Company',
        location: row.JobLocation || '',
        applicationDate: row.ApplicationDate || null,
        // applicationDate: row.ApplicationDate || row.CreatedOn || null,
        postinglink: row.PostingIdentifier || '',
        coverLetter: Boolean(row.SimilarRoles),
        referral: Boolean(row.SimilarCompany),
        referralContact: row.ReferralEmail || row.ReferredToEmail || '',
        referralContactName: row.ReferralName || row.ReferredToName || '',
          trackerStatus: row.OfferStatus || 'Applied',
          status: (() => {
            const current = (row.OfferStatus || '').trim();
            const allowed = ['Applied', 'Pursuing', 'Interviewing', 'Offer', 'Rejected', 'Not Pursuing'];
            return allowed.includes(current) ? current : 'Applied';
          })(),
        tasks: (row.tasks || []).map((t) => ({
          area: t.FocusAreaName || 'General',
          task: t.Title || '',
          diff: 'E',
        })),
        }));

        setJobs(normalized);
        setTotalPages(response.data?.total_pages || 1);
        setTotal(response.data?.total || 0);
    } catch (err) {
        console.error('Error fetching jobs:', err);
        setJobs([]);
        setTotalPages(1);
        setTotal(0);
    } finally {
        setLoading(false);
        setInitialLoad(false);
    }
      }, [page, perPage, selectedLocation, selectedPosition, dateFilter, debouncedSearchTerm, user, isFocusView]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Get unique positions from jobs
  const availablePositions = useMemo(() => {
    const positions = new Set();
    jobs.forEach(job => {
      if (job.title) {
        // Extract position type from title
        const titleWords = job.title.split(' ');
        if (titleWords.length > 0) {
          positions.add(job.title);
        }
      }
    });
    return Array.from(positions).sort();
  }, [jobs]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
      <header className="mb-5 md:mb-8">
        <h1 className={`text-2xl md:text-3xl font-bold mb-1 md:mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Job Tracker</h1>
        <p className={`text-sm md:text-base ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Keep track of your applications and their statuses</p>
      </header>

      {/* Search and Filter Bar */}
      <div className={`rounded-2xl border shadow-sm p-4 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search applications by title, company, location, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-100 placeholder:text-slate-400' : 'border-slate-300 bg-white text-black'
              }`}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                showFilters || activeFiltersCount > 0
                  ? 'bg-primary-600 text-white'
                  : isDarkMode
                    ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Filter size={18} />
              Filters
              {activeFiltersCount > 0 && (
                <span className="bg-white text-primary-600 px-2 py-0.5 rounded-full text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className={`rounded-2xl border shadow-sm p-6 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>Advanced Filters</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Position
              </label>
              <select
                value={selectedPosition}
                onChange={(e) => {
                  setSelectedPosition(e.target.value);
                  setPage(1);
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                }`}
              >
                <option value="">All Positions</option>
                {availablePositions.slice(0, 50).map((pos) => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                }`}
              >
                <option value="all">All Statuses</option>
                <option value="applied">Applied</option>
                <option value="pursuing">Pursuing</option>
                <option value="interviewing">Interviewing</option>
                <option value="offer">Offer</option>
                <option value="rejected">Rejected</option>
                <option value="not-pursuing">Not Pursuing</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Referral
              </label>
              <select
                value={referralFilter}
                onChange={(e) => {
                  setReferralFilter(e.target.value);
                  setPage(1);
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                }`}
              >
                <option value="all">All Referrals</option>
                <option value="Completed">Completed</option>
                <option value="Not Completed">Not Completed</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Cover Letter
              </label>
              <select
                value={coverLetterFilter}
                onChange={(e) => {
                  setCoverLetterFilter(e.target.value);
                  setPage(1);
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black bg-white'
                }`}
              >
                <option value="all">All Letters</option>
                <option value="Completed">Completed</option>
                <option value="Not Completed">Not Completed</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={handleClearFilters}
              className={`px-4 py-2 font-medium ${isDarkMode ? 'text-slate-300 hover:text-slate-100' : 'text-slate-600 hover:text-slate-800'}`}
            >
              Clear All
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {(activeFiltersCount > 0 || searchTerm) && (
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Active filters:</span>
          {searchTerm && (
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-full text-sm font-medium shadow-sm">
              Search: "{searchTerm}"
              <button onClick={() => {
                setSearchTerm('')
                setDebouncedSearchTerm('');
              }} className="hover:bg-primary-700 rounded-full p-0.5">
                <X size={14} />
              </button>
            </span>
          )}
          {selectedPosition && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Position: {selectedPosition}</span>}
          {dateFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Date: {dateFilter}</span>}
          {statusFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Status: {statusFilter}</span>}
          {referralFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Referral: {referralFilter}</span>}
          {coverLetterFilter !== 'all' && <span className="px-3 py-1 bg-primary-600 text-white rounded-full text-sm">Cover Letter: {coverLetterFilter}</span>}
        </div>
      )}

      <div className={`rounded-md border shadow-sm overflow-hidden mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className={`items-center hidden md:grid grid-cols-[4fr_2fr_2fr_2fr_2fr_2fr_2fr_1fr] gap-4 px-6 py-4 text-xs font-semibold uppercase border-b ${
          isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-white text-slate-700 border-slate-200'
        }`}>
          <div className='flex items-center gap-3'>
            <span>Application</span>
            {/* View Toggle */}
            <div className={`inline-flex rounded-lg border ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-slate-100'}`}>
                <button onClick={() => handleViewChange('focus')} className={segBtnClass(isFocusViewImmediate)}>
                    Focus
                </button>
                <button onClick={() => handleViewChange('archive')} className={segBtnClass(!isFocusViewImmediate)}>
                    Archive
                </button>
            </div>
          </div>
          <div>Company</div>
          <div>Location</div>
          <div className='pl-2'>Applied</div>
          <div>Cover Letter</div>
          <div>Referral</div>
          <div>Status</div>
          <div >Posting</div>
        </div>

        {/* Loading State */}
        {loading && initialLoad && (
          <JobTrackerSkeleton isDarkMode={isDarkMode} />
        )}

        {/* Application List */}
        {!initialLoad && (
          <Applications
            isDarkMode={isDarkMode}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            referralFilter={referralFilter}
            coverLetterFilter={coverLetterFilter}
            initialApplications={jobs}
            isFocusView={isFocusView}
          />
        )}
      </div>

      {!loading && (
        <>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`rounded-2xl border p-4 flex items-center justify-between ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
          }`}>
            <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} jobs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode 
                    ? 'border-slate-600 hover:bg-slate-700' 
                    : 'border-slate-300 hover:bg-slate-50'
                }`}
              >
                <ChevronLeft size={20} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
              </button>
                <span className={`text-sm px-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    isDarkMode 
                      ? 'border-slate-600 hover:bg-slate-700' 
                      : 'border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <ChevronRight size={20} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
              </button>
            </div>
          </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
